// src/components/map/hooks/adapters/useRigFracColorBy.ts
//
// Adapter hook: rig/frac layer color-by.
//
// Applies an attribute-driven icon-color match expression to the Rigs and Frac
// Crews layers independently, from each project layer's saved colorByField and
// colorOverrides. Colouring always uses the Plasma palette (rig/frac never
// inherit the well/facility palette); falls back to the static layer color for
// an empty or failed value domain. Re-applies after a style reload by keying on
// the MapContext isStyleLoaded flag (the same lifecycle that re-adds the
// layers), guarding every paint call with map.getLayer.
//
// This hook owns the icon-color PAINT only. Session-only value hiding (EVA-64)
// and the EVA-63 attribute filter both live in useRigFracLayerFilter.ts — the
// single setFilter writer for these layers — so a value-hide toggle, an
// attribute filter, and a map pan never clobber each other's layer filter.
//
// Also syncs each layer's colours onto the well focus when the layer's own
// Color By matches the focus and the focus has real colours ready
// (useFocusColorSync + resolveFocusPaint — see utils/color/focusColorSync).
// Evaluated per layer inside the loop below, never hoisted out of it, so Rigs
// and Frac Crews stay independent.
import { useEffect } from "react";

import {
  FRAC_COLOR,
  FRAC_LAYER,
  RIG_COLOR,
  RIG_FRAC_LAYER_TYPE,
  RIG_LAYER
} from "constants/mapLayers.constants";
import {
  selectHeldFocusColors,
  useFocusColorHoldStore
} from "stores/focusColorHoldStore";
import {
  selectRigFracColorByDetached,
  selectRigFracColorSource,
  selectRigFracFocusColorBy,
  useMapStore
} from "stores/map";
import { resolveFocusPaint } from "utils/color/focusColorSync";

import { useFocusColorSync } from "hooks/focusColorSync";
import { useRigFracBasePalette } from "hooks/rigfrac/useRigFracBasePalette";

import { buildRigFracColorAssignment } from "components/map/utils/rigFracColorAssignment";
import { resolveEffectiveColorByField } from "components/project/layers/components/rig-frac-details/rigFracColorByFields";
import {
  useProjectLayerListQuery,
  useRigFracValuesQuery
} from "components/project/layers/queries";
import { useProjectContext } from "components/project/projects/context";

import { useMapContext } from "../useMapContext";

export interface UseRigFracColorByParams {
  // Gated by the "Rig And Frac Layers" beta flag at the mount site.
  enabled?: boolean;
}

const STATIC_COLOR_BY_LAYER: Record<string, string> = {
  [RIG_LAYER]: RIG_COLOR,
  [FRAC_LAYER]: FRAC_COLOR
};

export function useRigFracColorBy(params: UseRigFracColorByParams = {}): void {
  const { enabled = true } = params;

  const { mapbox, isMapInitialized, isStyleLoaded } = useMapContext();
  const { selectedProjectId } = useProjectContext();

  const layerList = useProjectLayerListQuery({
    projectId: selectedProjectId,
    isAutoRefetching: enabled && !!selectedProjectId
  });
  const values = useRigFracValuesQuery({ enabled });

  const colorSourceByLayer = useMapStore(selectRigFracColorSource);
  const focusColorBy = useMapStore(selectRigFracFocusColorBy);
  const colorByDetached = useMapStore(selectRigFracColorByDetached);
  const focusResolution = useFocusColorSync();
  // What each attribute last resolved to. Shared with the layer panel and the
  // charts, so a surface that mounted late holds the same colours as one that
  // has been up the whole time.
  const heldFocusColors = useFocusColorHoldStore(selectHeldFocusColors);
  const basePalette = useRigFracBasePalette();

  const layers = layerList.data;
  const valueDomains = values.data?.values;

  // Icon-color paint. Keyed on the colour inputs only, so a hide/show toggle
  // never rebuilds and re-sets the match paint expression.
  useEffect(() => {
    if (!enabled || !mapbox || !isMapInitialized || !isStyleLoaded) {
      return;
    }

    const rigFracLayers = (layers ?? []).filter(
      (layer) => layer.projectLayerType === RIG_FRAC_LAYER_TYPE
    );

    for (const mapLayerId of [RIG_LAYER, FRAC_LAYER]) {
      if (!mapbox.getLayer(mapLayerId)) {
        continue;
      }

      const staticColor = STATIC_COLOR_BY_LAYER[mapLayerId];
      const layer = rigFracLayers.find((l) => l.name === mapLayerId);
      const field = resolveEffectiveColorByField({
        saved: layer?.colorByField,
        focusColorBy,
        isDetached: !!layer && !!colorByDetached[layer.layerId],
        layerName: mapLayerId
      });
      const domain = valueDomains?.[field];
      const colorSource = layer ? colorSourceByLayer[layer.layerId] : undefined;

      // The hold covers the focus colours only, never the whole paint: while a
      // matching trigger is pending this repaints with the colours this
      // attribute last resolved to, so an override edit, a refreshed value
      // domain or a style reload during that window still lands. Same call the
      // layer panel and the charts make, against the same held colours, so no
      // two surfaces can reach different answers for the same layer.
      const focus = resolveFocusPaint({
        resolution: focusResolution,
        layerAttribute: field,
        held: heldFocusColors[field]
      });

      const { matchExpression } = buildRigFracColorAssignment({
        colorByField: field,
        domain,
        colorSource,
        overrides: layer?.colorOverrides ?? {},
        staticColor,
        focus,
        defaultPalette: basePalette
      });

      mapbox.setPaintProperty(mapLayerId, "icon-color", matchExpression ?? staticColor);
    }
  }, [
    enabled,
    mapbox,
    isMapInitialized,
    isStyleLoaded,
    layers,
    valueDomains,
    colorSourceByLayer,
    focusColorBy,
    colorByDetached,
    heldFocusColors,
    focusResolution.state,
    focusResolution.attribute,
    focusResolution.colors,
    focusResolution.reservedColors,
    focusResolution.basePalette,
    basePalette
  ]);
}
